import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  CommunityModerationAction,
  CommunityModerationActionListResponse,
  CommunityModerationActionPayload,
  CommunityModerationKind,
  CommunityModerationUserSummary,
  CommunityQueueResponse,
  CommunityReport,
  CommunityReportListResponse,
  CommunityReportStatus,
  CommunityReportUpdatePayload
} from '../models/community-moderation.models';

export interface ModerationActionListOptions {
  userId?: number | null;
  kind?: CommunityModerationKind | '' | null;
  page?: number;
  perPage?: number;
}

export interface ReportListOptions {
  /** Empty = open + in review (the backend default). */
  status?: CommunityReportStatus | '' | null;
  channelId?: number | null;
  page?: number;
  perPage?: number;
}

/**
 * Moderation of the Community Network (`/api/community/moderation/*`, Phase 2):
 * the sanction protocol, reports and the approval queue.
 *
 * The backend answers 403 to anyone who moderates no channel at all; inside,
 * every list and every action is limited to the caller's scope.
 */
@Injectable({ providedIn: 'root' })
export class CommunityModerationService {
  private readonly base = '/community/moderation';

  constructor(private http: HttpClient) {}

  // --- Protocol ----------------------------------------------------------------

  listActions(options: ModerationActionListOptions = {}): Observable<CommunityModerationActionListResponse> {
    let params = new HttpParams()
      .set('page', String(options.page ?? 1))
      .set('per_page', String(options.perPage ?? 25));
    if (options.userId != null) params = params.set('user_id', String(options.userId));
    if (options.kind) params = params.set('kind', options.kind);
    return this.http.get<CommunityModerationActionListResponse>(`${this.base}/actions`, { params });
  }

  /** `moderation`, not `action`: Rails reserves params[:action] for the routed action. */
  createAction(moderation: CommunityModerationActionPayload): Observable<{ action: CommunityModerationAction }> {
    return this.http.post<{ action: CommunityModerationAction }>(`${this.base}/actions`, { moderation });
  }

  /** Writes the matching lift row (unmute/unban/unhide); 422 `community_already_lifted` on a second attempt. */
  liftAction(id: number, reason: string): Observable<{ action: CommunityModerationAction }> {
    return this.http.post<{ action: CommunityModerationAction }>(`${this.base}/actions/${id}/lift`, { reason });
  }

  getUserSummary(userId: number): Observable<CommunityModerationUserSummary> {
    return this.http.get<CommunityModerationUserSummary>(`${this.base}/users/${userId}`);
  }

  // --- Reports -----------------------------------------------------------------

  listReports(options: ReportListOptions = {}): Observable<CommunityReportListResponse> {
    let params = new HttpParams()
      .set('page', String(options.page ?? 1))
      .set('per_page', String(options.perPage ?? 25));
    if (options.status) params = params.set('status', options.status);
    if (options.channelId != null) params = params.set('channel_id', String(options.channelId));
    return this.http.get<CommunityReportListResponse>(`${this.base}/reports`, { params });
  }

  updateReport(id: number, report: CommunityReportUpdatePayload): Observable<{ report: CommunityReport }> {
    return this.http.patch<{ report: CommunityReport }>(`${this.base}/reports/${id}`, { report });
  }

  // --- Approval queue ------------------------------------------------------------

  getQueue(channelId?: number | null): Observable<CommunityQueueResponse> {
    let params = new HttpParams();
    if (channelId != null) params = params.set('channel_id', String(channelId));
    return this.http.get<CommunityQueueResponse>(`${this.base}/queue`, { params });
  }

  /**
   * Approve or reject a pending thread/post. A rejection with a reason also
   * writes a `warn` row for the author, so the reason is worth giving.
   */
  decide(type: 'thread' | 'post', id: number, approve: boolean, reason?: string | null): Observable<{ ok: boolean; approval_status: string }> {
    const body = reason?.trim() ? { reason: reason.trim() } : {};
    return this.http.post<{ ok: boolean; approval_status: string }>(
      `${this.base}/${type}s/${id}/${approve ? 'approve' : 'reject'}`,
      body
    );
  }
}
